import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, Globe, User, Menu, X, ChevronDown, Heart, LogOut, LayoutDashboard, Hotel, Utensils, Waves, MapPin, Trophy, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { COUNTRIES } from '@/data/mockData';
import { useCountry } from '@/contexts/CountryContext';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { Logo } from '@/components/Logo';
import { NotificationBell } from '@/components/NotificationBell';
import { cn } from '@/lib/utils';

export const Navbar = () => {
  const { country, setCountry } = useCountry();
  const { user, logout } = useAuth();
  const { lang, setLang, t, languages } = useI18n();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Marketplace browse menu — SEO-critical internal links
  const browseMenu = [
    { to: '/hotels',      label: t('nav.browse_hotels') || 'Hotels',         icon: Hotel },
    { to: '/restaurants', label: t('nav.browse_restaurants') || 'Restaurants', icon: Utensils },
    { to: '/beaches',     label: t('nav.browse_beaches') || 'Beaches',         icon: Waves },
    { to: '/attractions', label: t('nav.browse_attractions') || 'Attractions', icon: MapPin },
    { to: '/cafes',       label: t('nav.browse_cafes') || 'Cafés' },
    { to: '/bars',        label: t('nav.browse_bars') || 'Bars & Nightlife' },
    { to: '/tours',       label: t('nav.browse_tours') || 'Tours & Guides' },
  ];

  const links = [
    { to: '/plan-trip', label: t('nav.plan_trip'), highlight: true },
    { to: '/events', label: t('nav.events') },
    { to: '/map', label: t('nav.map') },
    { to: '/ask', label: t('nav.ask_local') },
    { to: '/emergency', label: t('nav.emergency') },
    { to: '/guides', label: t('nav.guides') },
    { to: '/pricing', label: t('nav.for_business') },
  ];

  const isAuthed = user && user.user_id;
  const isAdmin = isAuthed && ['moderator', 'admin', 'super_admin'].includes(user.role);

  return (
    <header className={cn(
      'sticky top-0 z-40 w-full bg-white transition-shadow',
      scrolled ? 'shadow-md' : 'shadow-sm border-b border-gray-200'
    )}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="navbar-logo" aria-label="InfoPoint Albania — home">
            <Logo size={36} />
            <div className="hidden sm:flex flex-col leading-none">
              <span className="font-heading font-bold text-base text-gray-900">InfoPoint {country.name}</span>
              <span className="text-[10px] text-gray-500 mt-0.5">Your Smart Guide</span>
            </div>
          </Link>

          {/* Desktop search trigger - compact icon button matching reference */}
          <Link to="/search" className="hidden md:inline-flex items-center justify-center w-9 h-9 rounded-md border border-gray-300 hover:border-gray-400 text-gray-500 transition-colors" aria-label="Search" data-testid="navbar-search-trigger">
            <Search className="w-4 h-4" />
          </Link>

          {/* Country switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="country-switcher" className="hidden md:flex items-center gap-2 h-9 px-3 rounded-md border border-gray-300 hover:border-gray-400 text-sm font-medium text-gray-700 transition-colors">
                <Globe className="w-4 h-4" />
                <span>{country.flag}</span>
                <span className="hidden lg:inline">{country.name}</span>
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <div className="px-2 py-1.5 text-xs uppercase tracking-wide text-gray-500">Available now</div>
              {COUNTRIES.filter(c => c.available).map(c => (
                <DropdownMenuItem key={c.code} data-testid={`country-option-${c.code}`} onClick={() => setCountry(c)} className="flex items-center gap-2 cursor-pointer">
                  <span className="text-lg">{c.flag}</span>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{c.name}</div>
                    <div className="text-xs text-gray-500">{c.nativeName}</div>
                  </div>
                  {country.code === c.code && <span className="w-2 h-2 rounded-full bg-[#00AA6C]" />}
                </DropdownMenuItem>
              ))}
              <div className="px-2 py-1.5 mt-1 border-t text-xs uppercase tracking-wide text-gray-500">Coming soon</div>
              {COUNTRIES.filter(c => !c.available).map(c => (
                <div key={c.code} className="flex items-center gap-2 px-2 py-1.5 text-gray-400 cursor-not-allowed text-sm">
                  <span className="text-lg grayscale">{c.flag}</span>
                  <span>{c.name}</span>
                  <span className="ml-auto text-[10px] uppercase tracking-wider bg-gray-100 px-1.5 py-0.5 rounded">soon</span>
                </div>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-[#00AA6C] transition-colors" data-testid="nav-browse">
                  {t('nav.browse')} <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {browseMenu.map((b) => (
                  <DropdownMenuItem key={b.to} asChild>
                    <Link to={b.to} className="flex items-center gap-2 cursor-pointer" data-testid={`nav-browse-${b.to.slice(1)}`}>
                      {b.icon && <b.icon className="w-4 h-4 text-gray-500" />}
                      <span>{b.label}</span>
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {links.map(l => (
              <Link key={l.to} to={l.to} className="flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-[#00AA6C] transition-colors" data-testid={`nav-${l.label.toLowerCase().replace(/ /g, '-')}`}>
                {l.icon && <l.icon className="w-4 h-4" />}
                {l.label}
              </Link>
            ))}
          </nav>

          {/* CTAs */}
          <div className="hidden md:flex items-center gap-2">
            {/* Language dropdown (EN/SQ/IT/DE) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="inline-flex items-center justify-center px-2 h-9 rounded-md border border-gray-300 hover:border-gray-400 text-xs font-bold text-gray-700 uppercase gap-1" data-testid="lang-toggle" aria-label="Change language">
                  {(languages.find((l) => l.code === lang) || languages[0]).flag} {lang.toUpperCase()} <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                {languages.map((l) => (
                  <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)} className={`cursor-pointer ${l.code === lang ? 'bg-gray-50 font-medium' : ''}`} data-testid={`lang-${l.code}`}>
                    <span className="mr-2">{l.flag}</span> {l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {isAuthed ? (
              <>
                <NotificationBell />
                <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-gray-300 hover:border-gray-400 text-sm font-medium text-gray-700" data-testid="nav-user-menu">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full" />
                    ) : (
                      <span className="w-6 h-6 rounded-full bg-[#00AA6C] text-white inline-flex items-center justify-center text-xs font-bold">
                        {(user.name || user.email || 'U')[0].toUpperCase()}
                      </span>
                    )}
                    <span className="max-w-[120px] truncate">{user.name || user.email}</span>
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem asChild>
                    <Link to="/passport" className="cursor-pointer" data-testid="nav-passport"><Trophy className="w-4 h-4 mr-2" /> My Passport</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/recommendations" className="cursor-pointer" data-testid="nav-recommendations"><Sparkles className="w-4 h-4 mr-2" /> For you</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/favorites" className="cursor-pointer" data-testid="nav-favorites"><Heart className="w-4 h-4 mr-2" /> {t('nav.favorites')}</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="cursor-pointer" data-testid="nav-dashboard"><LayoutDashboard className="w-4 h-4 mr-2" /> {t('nav.dashboard')}</Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="cursor-pointer" data-testid="nav-admin"><LayoutDashboard className="w-4 h-4 mr-2" /> Paneli Admin</Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-600" data-testid="nav-logout">
                    <LogOut className="w-4 h-4 mr-2" /> {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </>
            ) : (
              <>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/login" data-testid="nav-login"><User className="w-4 h-4" /> {t('nav.login')}</Link>
                </Button>
                <Button variant="green" size="sm" asChild>
                  <Link to="/register" data-testid="nav-register">{t('nav.register')}</Link>
                </Button>
              </>
            )}
            <Button variant="red" size="sm" asChild>
              <Link to="/claim" data-testid="nav-claim">{t('nav.claim')}</Link>
            </Button>
          </div>

          {/* Mobile */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="md:hidden w-9 h-9 inline-flex items-center justify-center rounded-md hover:bg-gray-100">
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88vw] sm:w-[380px] p-0">
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between p-5 border-b">
                  <div className="flex items-center gap-2">
                    <Logo size={36} />
                    <div className="leading-none">
                      <div className="font-heading font-bold text-sm">InfoPoint {country.name}</div>
                      <div className="text-[10px] text-gray-500">Your Smart Guide</div>
                    </div>
                  </div>
                  <button onClick={() => setMobileOpen(false)} className="w-9 h-9 inline-flex items-center justify-center rounded-md hover:bg-gray-100"><X className="w-5 h-5" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-1">
                  <Link to="/search" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2.5 rounded-md border border-gray-300 text-gray-500 text-sm mb-3">
                    <Search className="w-4 h-4" /> {t('common.search')}…
                  </Link>
                  <div className="px-3 py-2 text-xs uppercase tracking-wide text-gray-500">{t('nav.browse')}</div>
                  {browseMenu.map((b) => (
                    <Link key={b.to} to={b.to} onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2.5 rounded-md hover:bg-gray-50 text-sm">
                      {b.icon && <b.icon className="w-4 h-4 text-gray-500" />} {b.label}
                    </Link>
                  ))}
                  <div className="px-3 py-2 mt-2 text-xs uppercase tracking-wide text-gray-500">Country</div>
                  {COUNTRIES.filter(c => c.available).map(c => (
                    <button key={c.code} onClick={() => { setCountry(c); setMobileOpen(false); }} className={cn('w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm', country.code === c.code ? 'bg-gray-100 font-medium' : 'hover:bg-gray-50')}>
                      <span className="text-lg">{c.flag}</span> {c.name}
                    </button>
                  ))}
                  <div className="border-t my-3" />
                  {links.map(l => (
                    <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="flex items-center gap-2 px-3 py-2.5 rounded-md hover:bg-gray-50 text-sm font-medium text-gray-800">
                      {l.icon && <l.icon className="w-4 h-4" />} {l.label}
                    </Link>
                  ))}
                </div>
                <div className="p-5 border-t space-y-2">
                  {isAuthed ? (
                    <>
                      <Button variant="outline" className="w-full" asChild>
                        <Link to="/dashboard" onClick={() => setMobileOpen(false)}><LayoutDashboard className="w-4 h-4" /> {t('nav.dashboard')}</Link>
                      </Button>
                      <Button variant="outline" className="w-full" asChild>
                        <Link to="/passport" onClick={() => setMobileOpen(false)}><Trophy className="w-4 h-4" /> My Passport</Link>
                      </Button>
                      <Button variant="outline" className="w-full" asChild>
                        <Link to="/favorites" onClick={() => setMobileOpen(false)}><Heart className="w-4 h-4" /> {t('nav.favorites')}</Link>
                      </Button>
                      <Button variant="ghost" className="w-full text-red-600" onClick={() => { logout(); setMobileOpen(false); }}><LogOut className="w-4 h-4" /> {t('nav.logout')}</Button>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" className="w-full" asChild><Link to="/login" onClick={() => setMobileOpen(false)}><User className="w-4 h-4" /> {t('nav.login')}</Link></Button>
                      <Button variant="green" className="w-full" asChild><Link to="/register" onClick={() => setMobileOpen(false)}>{t('nav.register')}</Link></Button>
                    </>
                  )}
                  <Button variant="red" className="w-full" asChild><Link to="/claim" onClick={() => setMobileOpen(false)}>{t('nav.claim')}</Link></Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
